<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Icon from 'next/icon/Icon.vue';
import SidebarGroup from './SidebarGroup.vue';
import SuperAdminLink from './SuperAdminLink.vue';
import { useSidebarContext } from './provider';
import { matchesSidebarActiveRoute } from './managementMenu';

// Menu do celular no modelo do Vertical: atalhos fixos no topo e módulos que
// abrem as próprias opções, sem favoritos.
const props = defineProps({
  quickItems: { type: Array, default: () => [] },
  sections: { type: Array, default: () => [] },
  showSuperAdmin: { type: Boolean, default: false },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['navigate']);

const route = useRoute();
const router = useRouter();
const store = useStore();
const { isAllowed, resolvePath } = useSidebarContext();

const copy = {
  shortcuts: 'Atalhos',
  modules: 'Módulos',
  back: 'Voltar aos módulos',
};

const isFavoriteEntry = item => String(item.name).startsWith('Favorite ');

const visibleChildren = item =>
  (item.children || [])
    .flatMap(child => child.children || child)
    .filter(child => child.show !== false && child.to && isAllowed(child.to));

const isItemVisible = item => {
  if (item.show === false || isFavoriteEntry(item)) return false;
  if (item.to) return isAllowed(item.to);
  return visibleChildren(item).length > 0;
};

const itemIsActive = item => {
  const candidates = item.to
    ? [item, ...visibleChildren(item)]
    : visibleChildren(item);

  return candidates.some(candidate => {
    if (matchesSidebarActiveRoute(candidate, route)) return true;
    if (!candidate.to) return false;

    const candidatePath = resolvePath(candidate.to);
    return (
      route.path === candidatePath || route.path.startsWith(`${candidatePath}/`)
    );
  });
};

const tiles = computed(() => props.quickItems.filter(isItemVisible));

const modules = computed(() =>
  props.sections
    .filter(section => section.name !== 'section-favorites')
    .map(section => ({
      ...section,
      items: section.items.filter(isItemVisible),
    }))
    .filter(section => {
      if (section.direct && section.to) return isAllowed(section.to);
      if (!section.items.length) return false;
      // Módulo de uma opção só já coberto por um atalho não se repete.
      const linkedTile = tiles.value.find(
        tile => tile.sectionName === section.name
      );
      return !(linkedTile && section.items.length <= 1);
    })
);

const moduleIsActive = section =>
  section.direct && section.to
    ? itemIsActive(section)
    : section.items.some(itemIsActive);

const tileIsActive = tile => {
  if (tile.sectionName) {
    const linkedSection = props.sections.find(
      section => section.name === tile.sectionName
    );
    if (linkedSection?.items.some(itemIsActive)) return true;
  }
  return itemIsActive(tile);
};

const tileCount = tile => {
  const key = tile.getterKeys?.count;
  if (!key) return 0;
  const value = Number(store.getters[key]) || 0;
  return value;
};

const formatCount = value => (value > 99 ? '99+' : value);

const selectedModuleName = ref('');
const selectedModule = computed(() =>
  modules.value.find(section => section.name === selectedModuleName.value)
);

watch(
  () => props.isOpen,
  isOpen => {
    if (isOpen) selectedModuleName.value = '';
  }
);

watch(modules, sections => {
  if (
    selectedModuleName.value &&
    !sections.some(section => section.name === selectedModuleName.value)
  ) {
    selectedModuleName.value = '';
  }
});

const openTile = tile => {
  router.push(tile.to);
  emit('navigate');
};

const openModule = section => {
  selectedModuleName.value = section.name;
};

const backToModules = () => {
  selectedModuleName.value = '';
};

// Links de destino fecham o menu; grupos com subitens apenas expandem.
const handleListClick = event => {
  if (event.target.closest?.('a[href]')) emit('navigate');
};

const moduleSummary = section => {
  if (section.direct) return 'Abrir';
  const total = section.items.length;
  return total === 1 ? '1 opção' : `${total} opções`;
};
</script>

<template>
  <div class="mobile-navigation flex min-w-0 flex-col gap-4">
    <template v-if="!selectedModule">
      <section v-if="tiles.length" aria-labelledby="mobile-nav-shortcuts">
        <h2
          id="mobile-nav-shortcuts"
          class="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
        >
          {{ copy.shortcuts }}
        </h2>
        <ul class="m-0 grid list-none grid-cols-3 gap-2 p-0">
          <li v-for="tile in tiles" :key="tile.name" class="min-w-0">
            <button
              type="button"
              class="relative flex h-[4.5rem] w-full min-w-0 flex-col items-center justify-center gap-1.5 rounded-xl border px-1 transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
              :class="
                tileIsActive(tile)
                  ? 'border-n-brand/40 bg-n-brand/10 text-n-brand'
                  : 'border-n-weak bg-n-solid-1 text-n-slate-11 active:bg-n-alpha-2'
              "
              :aria-current="tileIsActive(tile) ? 'page' : undefined"
              @click="openTile(tile)"
            >
              <span class="relative">
                <Icon :icon="tile.icon" class="size-5" />
                <span
                  v-if="tileCount(tile)"
                  class="absolute -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-n-brand px-1 text-[10px] font-semibold leading-none text-white tabular-nums ltr:left-3 rtl:right-3"
                >
                  {{ formatCount(tileCount(tile)) }}
                </span>
              </span>
              <span
                class="w-full truncate text-center text-xs font-medium"
                :class="tileIsActive(tile) ? '' : 'text-n-slate-12'"
              >
                {{ tile.label }}
              </span>
            </button>
          </li>
        </ul>
      </section>

      <section v-if="modules.length" aria-labelledby="mobile-nav-modules">
        <h2
          id="mobile-nav-modules"
          class="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
        >
          {{ copy.modules }}
        </h2>
        <ul
          class="m-0 grid list-none overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 p-0"
          @click.capture="handleListClick"
        >
          <li
            v-for="section in modules"
            :key="section.name"
            class="min-w-0 border-n-weak [&:not(:last-child)]:border-b"
          >
            <RouterLink
              v-if="section.direct && section.to"
              :to="section.to"
              class="flex h-14 min-w-0 items-center gap-3 px-3 transition-colors active:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
              :aria-current="moduleIsActive(section) ? 'page' : undefined"
            >
              <span
                class="grid size-9 shrink-0 place-items-center rounded-lg"
                :class="
                  moduleIsActive(section)
                    ? 'bg-n-brand/15 text-n-brand'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                <Icon
                  :icon="section.icon || 'i-hugeicons-circle'"
                  class="size-[1.125rem]"
                />
              </span>
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-sm text-n-slate-12"
                  :class="
                    moduleIsActive(section) ? 'font-semibold' : 'font-medium'
                  "
                >
                  {{ section.label }}
                </span>
                <span class="block truncate text-xs text-n-slate-10">
                  {{ moduleSummary(section) }}
                </span>
              </span>
              <span
                class="i-hugeicons-arrow-right-01 size-4 shrink-0 text-n-slate-10 rtl:rotate-180"
                aria-hidden="true"
              />
            </RouterLink>
            <button
              v-else
              type="button"
              class="flex h-14 w-full min-w-0 items-center gap-3 px-3 text-start transition-colors active:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
              @click="openModule(section)"
            >
              <span
                class="grid size-9 shrink-0 place-items-center rounded-lg"
                :class="
                  moduleIsActive(section)
                    ? 'bg-n-brand/15 text-n-brand'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                <Icon
                  :icon="section.icon || 'i-hugeicons-circle'"
                  class="size-[1.125rem]"
                />
              </span>
              <span class="min-w-0 flex-1">
                <span
                  class="block truncate text-sm text-n-slate-12"
                  :class="
                    moduleIsActive(section) ? 'font-semibold' : 'font-medium'
                  "
                >
                  {{ section.label }}
                </span>
                <span class="block truncate text-xs text-n-slate-10">
                  {{ moduleSummary(section) }}
                </span>
              </span>
              <span
                v-if="moduleIsActive(section)"
                class="size-1.5 shrink-0 rounded-full bg-n-brand"
                aria-hidden="true"
              />
              <span
                class="i-hugeicons-arrow-right-01 size-4 shrink-0 text-n-slate-10 rtl:rotate-180"
                aria-hidden="true"
              />
            </button>
          </li>
          <li v-if="showSuperAdmin" class="min-w-0 border-t border-n-weak p-1">
            <SuperAdminLink />
          </li>
        </ul>
      </section>
    </template>

    <section v-else class="min-w-0" :aria-label="selectedModule.label">
      <div class="mb-3 flex min-w-0 items-center gap-2">
        <button
          type="button"
          class="grid size-10 shrink-0 place-items-center rounded-xl border border-n-weak bg-n-solid-1 text-n-slate-12 active:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
          :aria-label="copy.back"
          @click="backToModules"
        >
          <span
            class="i-hugeicons-arrow-left-01 size-5 rtl:rotate-180"
            aria-hidden="true"
          />
        </button>
        <span
          class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/15 text-n-brand"
          aria-hidden="true"
        >
          <Icon
            :icon="selectedModule.icon || 'i-hugeicons-circle'"
            class="size-5"
          />
        </span>
        <span class="min-w-0 leading-tight">
          <small class="block text-[11px] font-medium text-n-slate-10">
            {{ copy.modules }}
          </small>
          <strong
            class="block truncate text-base font-semibold text-n-slate-12"
          >
            {{ selectedModule.label }}
          </strong>
        </span>
      </div>
      <ul
        class="m-0 flex min-w-0 list-none flex-col gap-1 p-0"
        @click.capture="handleListClick"
      >
        <SidebarGroup
          v-for="item in selectedModule.items"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </section>
  </div>
</template>
